/**
 * Copies text to the clipboard. Falls back to
 * `document.execCommand('copy')` for browsers/contexts where the async
 * Clipboard API is unavailable or blocked (e.g., iPad Safari when the
 * triggering element has lost focus after a menu closes).
 *
 * Returns true when the copy succeeded, false otherwise.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof window === "undefined") return false;

  const copyViaExecCommand = (): boolean => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "0";
      textarea.style.left = "0";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  try {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    return copyViaExecCommand();
  } catch {
    return copyViaExecCommand();
  }
}
